import React from 'react';
import useAccounts from '~/hooks/useAccounts';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  SELECTED_ACCOUNT_STORAGE_KEY,
} from '~/util/localStorageKeys';
import { navigate } from '@gatsbyjs/reach-router';
import { AccountStatus } from '~/graphql/types';
import Dropdown, {
  SelectedOption,
  type Option,
} from '~/components/molecule/Dropdown';
import type { IconType } from '~/components/atom/Icon';
import { AUTH_PATHS } from '~/components/page/Auth/constants';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Label } from '~/components/atom/Typography';
import type { ThemeColor } from '~/theme/System/tokens/colorPalette';
import OptionsButton from '../OptionsButton';
import type { InteractiveAppearance } from '~/styles/constants';

const text = {
  logout: 'Uitloggen',
};
export type Props = {
  dataTestId?: string;
  hideWhenSingleAccount?: boolean;
  labelProps?: {
    text: string;
    color: ThemeColor;
  } | null;
  variant?: 'button' | 'dropdown';
  appearance?: InteractiveAppearance;
  withLogoutOption?: boolean;
};

const logoutOption = {
  label: text.logout,
  icon: { name: 'sign-out' as IconType },
  payload: AUTH_PATHS.LOGOUT,
  key: 'nav-link-settings-dropdown-signout',
  styleOptions: {
    lineAbove: true,
  },
};

export const AccountSwitcher: React.FC<Props> = ({
  hideWhenSingleAccount = false,
  labelProps = null,
  variant = 'dropdown',
  appearance = 'accent',
  withLogoutOption = true,
}) => {
  const selectedAccountIdFromStorage = getLocalStorageItem(
    SELECTED_ACCOUNT_STORAGE_KEY,
  );
  const currentAccount = useCurrentAccount();

  // !important: Do not remove the optional check from currentAccount otherwise it breaks
  const selectedAccountId = selectedAccountIdFromStorage ?? currentAccount?.id;
  const accounts = useAccounts();

  if (accounts.length === 0 || (hideWhenSingleAccount && accounts.length === 1))
    return null;

  const selectedAccount = accounts.find(
    account => account.id === selectedAccountId,
  );

  const handleSelectOption = (option: Option) => {
    if (option.payload === selectedAccountId) return;
    if (option.payload === AUTH_PATHS.LOGOUT) {
      if (selectedAccount?.status === AccountStatus.Cancelled) {
        removeLocalStorageItem(SELECTED_ACCOUNT_STORAGE_KEY);
      }

      return navigate(option.payload);
    }

    global.localStorage.setItem(SELECTED_ACCOUNT_STORAGE_KEY, option.payload);

    // Force reload
    return (global.window.location.href = '/-/');
  };

  const options: Array<Option> = [
    ...accounts.map(account => ({
      label: account.name,
      icon: { name: 'briefcase' as IconType },
      payload: account.id,
      key: account.id,
      onClickAction: () =>
        handleSelectOption({ payload: account.id } as Option),
    })),
    ...(withLogoutOption
      ? [
          {
            ...logoutOption,
            onClickAction: () =>
              handleSelectOption({ payload: AUTH_PATHS.LOGOUT } as Option),
          },
        ]
      : []),
  ];

  const onChange = (selectedOption: SelectedOption) => {
    const { option } = selectedOption;
    return handleSelectOption(option);
  };

  return (
    <JustificationContainer direction="column" width="100%" gap="xxxs">
      {labelProps && (
        <Label margin={[null]} color={labelProps.color}>
          {labelProps.text}
        </Label>
      )}
      {variant === 'button' ? (
        <OptionsButton
          icon="briefcase"
          label="Account"
          options={options}
          selectedOption={selectedAccountId}
          appearance={appearance}
        />
      ) : (
        <Dropdown
          options={options}
          selectedOptionIdx={options.findIndex(
            option => option.key === selectedAccountId,
          )}
          onChange={onChange}
          dataTestId="global-account-switcher"
        />
      )}
    </JustificationContainer>
  );
};

export default AccountSwitcher;
